/**
 * Planning Mode Sidebar
 *
 * Commitment status + two capabilities (Viability / Launch planning) that share
 * Engine scenario×scope, plus people/structure and plan operations.
 *
 * Supports an icon-rail minimized mode (uiStore.sidebarOpen === false), including
 * the bottom workspace / space / user strip.
 */

import { Link, useLocation, useMatch } from 'react-router-dom';
import {
  Zap,
  User,
  ChevronLeft,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { PrioritiesLogo } from '../ui/PrioritiesLogo';
import { SpaceSwitcher } from '../prioritization/SpaceSwitcher';
import { TenantSwitcher } from '../prioritization/TenantSwitcher';
import { ProductModeSwitcher } from '../prioritization/ProductModeSwitcher';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
import { useTimePhasedPlanningEnabled } from '../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import { setSettingsEntryProduct } from '../../lib/productSurfacePaths';
import { useAppArea } from '../../lib/appAreas';
import { useAreaReports } from '../../hooks/prioritization/useAreaReports';
import { actionMenu, plansMenu, planSectionLinks, HELP_MENU, SHARED_BOTTOM, SHARED_TOP, type AreaMenuItem, type AreaMenuSection } from '../../lib/areaMenus';
import { useModuleGate } from '../../lib/moduleGate';
import { monoIconButton } from '@/components/mono/iconButton';
import { useContributorExperience, useRoleMenu } from '../../hooks/prioritization/useContributorExperience';
import { roleMenuHasLink } from '../../lib/contributorNavProfiles';

function roleLabel(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const LAUNCH_ENGINE_SUBS = new Set(['participation', 'time-phased', 'funding']);

function engineSubPath(pathname: string): string {
  if (!pathname.startsWith('/planning/engine')) return '';
  return pathname.slice('/planning/engine'.length).replace(/^\/+/, '').split('/')[0] ?? '';
}

/** Launch capability owns LC + launch-specific Engine inputs. */
function isLaunchPlanningPath(pathname: string): boolean {
  if (pathname.startsWith('/planning/launch-calendar')) return true;
  if (!pathname.startsWith('/planning/engine')) return false;
  return LAUNCH_ENGINE_SUBS.has(engineSubPath(pathname));
}

/** Viability owns Engine overview, viability inputs, and viability results. */
function isViabilityPath(pathname: string): boolean {
  if (!pathname.startsWith('/planning/engine')) return false;
  return !isLaunchPlanningPath(pathname);
}

type NavItem = AreaMenuItem;
type NavSection = AreaMenuSection;

export function PlanningSidebar() {
  const location = useLocation();
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  const planMatch = useMatch('/planning/plans/:cycleId/*');
  const planMatchExact = useMatch('/planning/plans/:cycleId');
  const activeCycleId = (planMatch ?? planMatchExact)?.params?.cycleId ?? null;

  const userName = useUserStore((s) => s.userName);
  const currentRole = useUserStore((s) => s.currentRole);
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);
  const permissionsLoaded = useUserStore((s) => s.permissionsLoaded);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const collapsed = !sidebarOpen;
  // Members on the role-based menu get the short Plans menu, built by the same
  // builder as the Priorities one; admins and a workspace with the switch off
  // keep the full menu.
  const { useContributorShell } = useContributorExperience();
  const roleMenu = useRoleMenu('plans');
  const allowed = (href: string) => !useContributorShell || roleMenuHasLink(roleMenu, href);

  // The four areas (src/lib/appAreas.ts): this shell shows Plans or Action, between the shared entries.
  const area = useAppArea(location.pathname);
  const evidenceEnabled = useModuleGate('evidence');
  // Reports and Decisions share the Reports gate, as in the Priorities sidebar.
  const reportsEnabled = useModuleGate('reports');
  // The Reports menu lists the reports placed in this area (Settings → Reports).
  const areaReports = useAreaReports(area);
  const areaSections: NavSection[] = area === 'action' ? actionMenu({ evidenceEnabled }) : plansMenu({ timePhasedEnabled });
  const fullSections: NavSection[] = [
    { label: '', items: SHARED_TOP },
    ...areaSections,
    ...(reportsEnabled
      ? [
          { label: 'Reports', items: areaReports },
          { label: '', items: SHARED_BOTTOM.filter((item) => item.href !== '/prioritization/reports') },
        ]
      : []),
  ];

  const fullByHref = new Map(fullSections.flatMap((section) => section.items).map((item) => [item.href, item]));
  const sections: NavSection[] = useContributorShell
    ? roleMenu.sections.map((section) => ({
        label: section.label,
        items: section.links.flatMap((link) => {
          const item = fullByHref.get(link.href);
          return item ? [{ ...item, name: link.label }] : [];
        }),
      }))
    : fullSections;

  const isActive = (item: NavItem) => {
    const { pathname } = location;
    if (item.match === 'exact') return pathname === item.href;
    if (item.match === 'viability') return isViabilityPath(pathname);
    if (item.match === 'launch') return isLaunchPlanningPath(pathname);
    const href = item.href.split('?')[0];
    if (href === '/planning/plans') {
      return pathname.startsWith('/planning/plans') || pathname.startsWith('/planning/plan-of-record');
    }
    return pathname === href || pathname.startsWith(href + '/');
  };

  const roleSummary =
    permissionsLoaded && (assignedRoleNames?.length ?? 0) > 0
      ? [currentRole, ...(assignedRoleNames ?? [])]
          .filter((r): r is string => typeof r === 'string' && r.length > 0)
          .filter((r, i, a) => a.indexOf(r) === i)
          .map(roleLabel)
          .join(' · ')
      : '';

  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 flex flex-col border-r border-[var(--rule)] bg-[var(--bg-raised)] transition-[width] duration-200 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div
        className={`flex h-14 shrink-0 items-center border-b border-[var(--rule)] dark:border-[var(--rule)] ${
          collapsed ? 'justify-center px-1' : 'justify-between px-3'
        }`}
      >
        <Link
          to="/planning"
          className="flex items-center leading-none"
          title={collapsed ? 'Plans home' : undefined}
        >
          <PrioritiesLogo height={28} markOnly={collapsed} />
        </Link>
        {!collapsed && (
          <button
            type="button"
            onClick={() => toggleSidebar()}
            className={monoIconButton('sm')}
            title="Minimize navigation"
            aria-label="Minimize navigation"
          >
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>
      <ProductModeSwitcher collapsed={collapsed} />

      <nav
        className={`flex-1 overflow-y-auto py-3 space-y-4 ${
          collapsed ? 'px-1.5' : 'px-3'
        }`}
      >
        {[...sections, HELP_MENU].map((section, si) => (
          <div key={`${section.label}-${si}`}>
            {!collapsed && section.label && (
              <div className="px-3 pb-1 text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wider">
                {section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(item);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    title={collapsed ? item.name : undefined}
                    aria-label={item.name}
                    className={`flex items-center  text-sm font-medium transition-all ${
                      collapsed
                        ? 'justify-center h-10 w-10 mx-auto'
                        : 'gap-3 px-3 py-2.5'
                    } ${
                      active
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                        : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
                    }`}
                  >
                    <item.icon
                      className={`h-5 w-5 flex-shrink-0 ${
                        active
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                      }`}
                    />
                    {!collapsed && <span>{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {activeCycleId && (
          <div className="pt-1">
            {!collapsed && (
              <div className="px-3 mb-1 flex items-center gap-2">
                <Link
                  to="/planning/plans?catalog=1"
                  className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] transition-colors"
                >
                  <ChevronLeft className="h-3 w-3" />
                  All plans
                </Link>
                <span className="text-xs text-[var(--text-ter)] mx-1">/</span>
                <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wide truncate">
                  Current plan
                </span>
              </div>
            )}
            <div className="space-y-0.5">
              {planSectionLinks(activeCycleId).filter((item) => allowed(item.gate)).map((item) => {
                const active =
                  item.match === 'exact'
                    ? location.pathname === item.href
                    : location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    title={collapsed ? item.name : undefined}
                    aria-label={item.name}
                    className={`flex items-center  text-sm font-medium transition-all ${
                      collapsed
                        ? 'justify-center h-9 w-9 mx-auto'
                        : 'gap-3 px-3 py-2'
                    } ${
                      active
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                        : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
                    }`}
                  >
                    <item.icon
                      className={`flex-shrink-0 ${collapsed ? 'h-4 w-4' : 'h-4 w-4'} ${
                        active
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
                      }`}
                    />
                    {!collapsed && <span>{item.name}</span>}
                  </Link>
                );
              })}
            </div>
            {allowed('/planning/engine') && (
            <Link
              to="/planning/engine"
              title={collapsed ? 'Open Viability' : undefined}
              aria-label="Open Viability"
              className={`flex items-center  text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors font-medium mt-1 ${
                collapsed ? 'justify-center h-9 w-9 mx-auto' : 'gap-2 px-3 py-2'
              }`}
            >
              <Zap className="h-3.5 w-3.5 flex-shrink-0" />
              {!collapsed && 'Open Viability'}
            </Link>
            )}
          </div>
        )}

        {/* Settings — in the scrollable nav (same placement as Priorities), not the sticky footer */}
        <div className="pt-1">
          <Link
            to="/prioritization/settings"
            onClick={() => setSettingsEntryProduct('planning')}
            title={collapsed ? 'Settings' : undefined}
            aria-label="Settings"
            className={`flex items-center  text-sm font-medium transition-all ${
              collapsed ? 'justify-center h-10 w-10 mx-auto' : 'gap-3 px-3 py-2.5'
            } ${
              location.pathname.startsWith('/prioritization/settings')
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            <Settings
              className={`h-5 w-5 flex-shrink-0 ${
                location.pathname.startsWith('/prioritization/settings')
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'
              }`}
            />
            {!collapsed && <span>Settings</span>}
          </Link>
        </div>
      </nav>

      {/* Workspace + account — fixed strip below scrollable nav (matches Priorities) */}
      <div
        className={`flex-shrink-0 border-t border-[var(--rule)] dark:border-[var(--rule)] space-y-2 ${
          collapsed ? 'p-2 flex flex-col items-center' : 'p-4 space-y-3'
        }`}
      >
        <TenantSwitcher compact iconOnly={collapsed} />
        <SpaceSwitcher iconOnly={collapsed} />
        <div
          className={`flex items-center min-w-0 ${
            collapsed ? 'justify-center' : 'gap-2 px-3 py-2'
          }`}
          title={collapsed ? [userName, roleSummary].filter(Boolean).join(' · ') : undefined}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-shrink-0">
            <User className="h-4 w-4" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div
                className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate"
                title={userName ?? ''}
              >
                {userName}
              </div>
              {roleSummary && (
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate">
                  {roleSummary}
                </div>
              )}
            </div>
          )}
        </div>
        {collapsed && (
          <button
            type="button"
            onClick={() => toggleSidebar()}
            className="inline-flex h-10 w-10 items-center justify-center text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-sec)]"
            title="Expand navigation"
            aria-label="Expand navigation"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
